import { formatDate } from 'date-fns';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { indexBy } from '@trello/arrays';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { getNonPublicIfAvailable } from '@trello/business-logic/member';
import { getAddMembershipErrorType } from '@trello/business-logic/membership';
import { sendErrorEvent } from '@trello/error-reporting';
import { Button } from '@trello/nachos/button';
import { Checkbox, useToggleableCheckboxes } from '@trello/nachos/checkbox';
import { showFlag } from '@trello/nachos/experimental-flags';
import { HelpIcon } from '@trello/nachos/icons/help';
import { Tooltip } from '@trello/nachos/tooltip';
import type { PIIString } from '@trello/privacy';
import {
  dangerouslyConvertPrivacyString,
  EMPTY_PII_STRING,
} from '@trello/privacy';
import type { WorkspaceRequestAccessTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { useWorkspaceUserLimit } from '@trello/workspaces/user-limit';

import { ShowNewLicenseInviteErrFlag } from 'app/src/components/WorkspaceInviteModal/ShowNewLicenseInviteErrFlag';
import {
  useWorkspaceJoinRequestPopover,
  WorkspaceJoinRequestPopover,
} from 'app/src/components/WorkspaceJoinRequestPopover';
import { WorkspaceMembersFilterBox } from 'app/src/components/WorkspaceMembersFilterBox';
import { useApproveWorkspaceJoinRequestMutation } from './ApproveWorkspaceJoinRequestMutation.generated';
import { useDeleteWorkspaceJoinRequestMutation } from './DeleteWorkspaceJoinRequestMutation.generated';
import { getAddMembershipErrorString } from './getAddMembershipErrorString';
import { SelectedRequestsExceedLimitMessage } from './SelectedRequestsExceedLimitMessage';
import { useWorkspaceJoinRequestList } from './useWorkspaceJoinRequestList';
import { WorkspaceJoinRequestItem } from './WorkspaceJoinRequestItem';
import {
  useWorkspaceJoinRequestListQuery,
  WorkspaceJoinRequestListDocument,
} from './WorkspaceJoinRequestListQuery.generated';
import { useWorkspaceJoinRequestMemberLazyQuery } from './WorkspaceJoinRequestMemberQuery.generated';

import styles from './WorkspaceJoinRequestList.less';

interface WorkspaceJoinRequestListProps {
  orgId: string;
  orgType: 'bc' | 'enterprise' | 'free' | 'standard';
  orgMemberLimit?: number;
  orgIdEnterprise?: string;
  orgEnterpriseName?: string;
}

export const WorkspaceJoinRequestList: FunctionComponent<
  WorkspaceJoinRequestListProps
> = ({
  orgId,
  orgType,
  orgMemberLimit,
  orgIdEnterprise,
  orgEnterpriseName,
}) => {
  const intl = useIntl();

  const { data: workspaceJoinRequestsData } = useWorkspaceJoinRequestListQuery({
    variables: { orgId },
    waitOn: ['MemberHeader'],
    skip: !orgId,
  });

  const { collaboratorCount, isAtLimit, isOverLimit, isWorkspaceFree } =
    useWorkspaceUserLimit({
      workspaceId: orgId,
    });

  const isAtOrOverLimit = isAtLimit || isOverLimit;

  const guestMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    workspaceJoinRequestsData?.organization?.collaborators?.forEach(
      ({ id }) => {
        map[id] = true;
      },
    );
    return map;
  }, [workspaceJoinRequestsData?.organization?.collaborators]);

  const [loadJoinRequestMemberData] = useWorkspaceJoinRequestMemberLazyQuery();

  const [nameFilter, setNameFilter] = useState('');

  const onNameFilterChange = useCallback((value: string) => {
    setNameFilter(value);
  }, []);

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'requestWorkspaceAccessAdminScreen',
      containers: formatContainers({ workspaceId: orgId }),
    });
  }, [orgId]);

  const { accessRequests } = useWorkspaceJoinRequestList({
    orgId,
  });

  const [addMemberToOrg] = useApproveWorkspaceJoinRequestMutation();
  const [deleteAccessRequest] = useDeleteWorkspaceJoinRequestMutation();

  const {
    triggerRefApproveWorkspaceJoinRequestPopover,
    toggleApproveWorkspaceJoinRequestPopover,
    hideApproveWorkspaceJoinRequestPopover,
    approveWorkspaceJoinRequestPopoverProps,
    triggerRefDeletePopover,
    toggleDeletePopover,
    hideDeletePopover,
    deletePopoverProps,
  } = useWorkspaceJoinRequestPopover();

  const { checkboxes, selectedCheckboxLength, toggleSelectAll, clearAll } =
    useToggleableCheckboxes({
      values: accessRequests.map(({ idMemberRequestor }) => idMemberRequestor),
    });
  const checkedRequests = checkboxes.checkboxState as string[];

  // variable holds a count of selected members that would add to the workspace's collaborator count
  // existing workspace guests would *not* add to the count
  const newCollaboratorSelectionCount = useMemo(() => {
    let count = 0;
    for (const memberId of checkedRequests) {
      if (!guestMap[memberId]) {
        count++;
      }
    }
    return count;
  }, [checkedRequests, guestMap]);

  const isSelectedRequestsExceedLimitMessageVisible =
    isWorkspaceFree &&
    checkedRequests.length > 0 &&
    newCollaboratorSelectionCount > 0 &&
    newCollaboratorSelectionCount + collaboratorCount > 10;

  const [selectAllCheckbox, setSelectAllCheckbox] = useState<
    'all' | 'none' | 'partial'
  >('none');

  const isAddSelectedButtonEnabled = useMemo(() => {
    // enable the button if all selected members are already workspace guests since this won't affect the collaborator count
    if (checkedRequests.length > 0 && newCollaboratorSelectionCount === 0) {
      return true;
    }

    // enable the button if the selected members would not exceed the workspace's collaborator limit
    if (
      checkedRequests.length > 0 &&
      newCollaboratorSelectionCount + collaboratorCount <= 10
    ) {
      return true;
    }
    return false;
  }, [
    checkedRequests.length,
    collaboratorCount,
    newCollaboratorSelectionCount,
  ]);
  const isDeleteSelectedButtonEnabled = useMemo(() => {
    if (checkedRequests.length > 0) {
      return true;
    }
    return false;
  }, [checkedRequests.length]);

  const handleSelectAll = useCallback(() => {
    if (selectAllCheckbox === 'none') {
      setSelectAllCheckbox('all');
      toggleSelectAll();
      Analytics.sendClickedButtonEvent({
        buttonName: 'selectAllPendingMembersButton',
        source: 'workspaceRequestsScreen',
        containers: formatContainers({ workspaceId: orgId }),
        attributes: {
          requestors: checkedRequests,
        },
      });
      Analytics.sendUIEvent({
        action: 'checked',
        actionSubject: 'checkbox',
        actionSubjectId: 'pendingMemberCheckbox',
        source: 'workspaceRequestsScreen',
        containers: formatContainers({ workspaceId: orgId }),
      });
    }
    if (selectAllCheckbox === 'all' || selectAllCheckbox === 'partial') {
      setSelectAllCheckbox('none');
      clearAll();
      Analytics.sendUIEvent({
        action: 'unchecked',
        actionSubject: 'checkbox',
        actionSubjectId: 'pendingMemberCheckbox',
        source: 'workspaceRequestsScreen',
        containers: formatContainers({ workspaceId: orgId }),
      });
    }
  }, [orgId, checkedRequests, clearAll, selectAllCheckbox, toggleSelectAll]);

  const uncheckAll = useCallback(() => {
    clearAll();
    setSelectAllCheckbox('none');
    Analytics.sendUIEvent({
      action: 'unchecked',
      actionSubject: 'checkbox',
      actionSubjectId: 'pendingMemberCheckbox',
      source: 'workspaceRequestsScreen',
      containers: formatContainers({ workspaceId: orgId }),
    });
  }, [clearAll, orgId]);

  const onIndividualCheckboxChange = useCallback(
    (action: 'checked' | 'unchecked') => {
      const newLength =
        action === 'checked'
          ? selectedCheckboxLength + 1
          : selectedCheckboxLength - 1;
      if (newLength === accessRequests.length) {
        setSelectAllCheckbox('all');
      } else if (newLength === 0) {
        setSelectAllCheckbox('none');
      } else if (selectAllCheckbox !== 'partial') {
        setSelectAllCheckbox('partial');
      }
    },
    [accessRequests.length, selectAllCheckbox, selectedCheckboxLength],
  );

  const showErrorFlag = useCallback(
    (error: string, user: string, workspaceId?: string) => {
      const errorMessage = getAddMembershipErrorType(error);
      if (errorMessage === 'unauthorized-licensed-invite') {
        ShowNewLicenseInviteErrFlag(
          [user],
          workspaceId!,
          workspaceJoinRequestsData?.organization?.prefs
            ?.newLicenseInviteRestrictUrl,
        );
        return;
      }

      const workspaceMemberLimit = String(orgMemberLimit);
      const title = getAddMembershipErrorString(
        errorMessage,
        user,
        workspaceMemberLimit,
      );

      showFlag({
        id: 'acceptRequestAccessToJoinError',
        seed: user,
        title,
        appearance: 'error',
        isAutoDismiss: true,
        msTimeout: 8000,
      });
    },
    [
      orgMemberLimit,
      workspaceJoinRequestsData?.organization?.prefs
        ?.newLicenseInviteRestrictUrl,
    ],
  );

  const showSuccessFlag = useCallback(
    (user: string) => {
      showFlag({
        id: 'acceptRequestAccessToJoinSuccess',
        seed: user,
        title: intl.formatMessage(
          {
            id: 'templates.workspace_join_requests.added',
            defaultMessage: '{user} was added.',
            description:
              'Success message when adding a member to the workspace',
          },
          {
            user,
          },
        ),
        appearance: 'success',
        isAutoDismiss: true,
        msTimeout: 8000,
      });
    },
    [intl],
  );

  const addMembersToWorkspace = useCallback(
    async (onComplete?: () => void) => {
      const remainingAccessRequests = indexBy(
        accessRequests,
        'idMemberRequestor',
      );

      const addMemberToWorkspacePromises = [];

      for (const memberId of checkedRequests) {
        const taskName = 'edit-organization/members/add';
        const source = 'workspaceRequestsScreen';
        const traceId = Analytics.startTask({
          taskName,
          source,
        });

        let username: string,
          fullName: PIIString | null | undefined,
          user: string;

        addMemberToWorkspacePromises.push(
          loadJoinRequestMemberData({
            variables: { id: memberId },
          })
            .then(({ data }) => {
              username = dangerouslyConvertPrivacyString(
                data?.member?.username ?? EMPTY_PII_STRING,
              );
              fullName =
                (data?.member &&
                  getNonPublicIfAvailable(data.member, 'fullName')) ??
                EMPTY_PII_STRING;

              user =
                fullName !== EMPTY_PII_STRING
                  ? `${dangerouslyConvertPrivacyString(fullName)} (${username})`
                  : username;

              return addMemberToOrg({
                variables: {
                  user: { id: memberId },
                  workspaceId: orgId,
                  traceId,
                },
                refetchQueries: [
                  {
                    query: WorkspaceJoinRequestListDocument,
                    variables: { orgId },
                    context: {
                      operationName: 'RefetchWorkspaceJoinRequestList',
                      document: WorkspaceJoinRequestListDocument,
                    },
                  },
                ],
              });
            })
            .then(({ errors }) => {
              if (errors) {
                throw errors;
              }
              Analytics.taskSucceeded({
                taskName,
                source,
                traceId,
              });
              Analytics.sendTrackEvent({
                action: 'added',
                actionSubject: 'member',
                source,
                containers: {
                  organization: {
                    id: orgId,
                  },
                },
                attributes: {
                  requestor: memberId,
                },
              });
              remainingAccessRequests.delete(memberId);
              showSuccessFlag(user);
            })
            .catch((error) => {
              Analytics.taskFailed({
                taskName,
                source,
                error,
                traceId,
              });

              sendErrorEvent(error, {
                tags: {
                  ownershipArea: 'trello-web-eng',
                  feature: 'Workspace Membership Request',
                },
                extraData: {
                  component: 'WorkspaceJoinRequestList',
                },
              });

              showErrorFlag(error.message, user, orgId);
            }),
        );
      }

      Promise.all(addMemberToWorkspacePromises).then(() => {
        onComplete?.();
        uncheckAll();
      });
    },
    [
      uncheckAll,
      accessRequests,
      checkedRequests,
      addMemberToOrg,
      orgId,
      showErrorFlag,
      showSuccessFlag,
      loadJoinRequestMemberData,
    ],
  );

  // APPROVE REQUESTS
  const onClickAddAllToWorkspace = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'addAllToWorkspaceButton',
      source: 'workspaceRequestsScreen',
      containers: formatContainers({ workspaceId: orgId }),
      attributes: {
        requestors: checkedRequests,
      },
    });
    if (orgType !== 'free') {
      // opens popover
      toggleApproveWorkspaceJoinRequestPopover();
    } else {
      addMembersToWorkspace();
    }
  }, [
    toggleApproveWorkspaceJoinRequestPopover,
    orgId,
    checkedRequests,
    addMembersToWorkspace,
    orgType,
  ]);

  // popover with confirmation for adding to workspace
  const onConfirmAddToWorkspace = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'addToWorkspaceButton',
      source: 'approvePendingMembersInlineDialog',
      containers: formatContainers({ workspaceId: orgId }),
      attributes: {
        requestors: checkedRequests,
      },
    });

    addMembersToWorkspace(toggleApproveWorkspaceJoinRequestPopover);
  }, [
    orgId,
    toggleApproveWorkspaceJoinRequestPopover,
    checkedRequests,
    addMembersToWorkspace,
  ]);

  // DELETE REQUESTS
  const onClickDelete = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'deleteButton',
      source: 'workspaceRequestsScreen',
      containers: formatContainers({ workspaceId: orgId }),
      attributes: {
        requestors: checkedRequests,
      },
    });
    toggleDeletePopover();
  }, [toggleDeletePopover, orgId, checkedRequests]);

  // popover with confirmation for rejecting requests
  const onConfirmDelete = useCallback(async () => {
    const source = 'deletePendingMembersInlineDialog';
    const taskName = 'delete-requestAccess/organization';
    Analytics.sendClickedButtonEvent({
      buttonName: 'deleteButton',
      source,
      containers: formatContainers({ workspaceId: orgId }),
      attributes: {
        requestors: checkedRequests,
      },
    });

    const deleteAccessRequestPromises = [];

    for (const memberId of checkedRequests) {
      const traceId = Analytics.startTask({
        taskName,
        source,
      });
      deleteAccessRequestPromises.push(
        deleteAccessRequest({
          variables: {
            traceId,
            modelType: 'organization',
            id: orgId,
            idInvitedMember: memberId,
            signature:
              accessRequests.find(
                ({ idMemberRequestor }) => idMemberRequestor === memberId,
              )?.signature || '',
          },
          refetchQueries: [
            {
              query: WorkspaceJoinRequestListDocument,
              variables: { orgId },
              context: {
                operationName: 'RefetchWorkspaceJoinRequestList',
                document: WorkspaceJoinRequestListDocument,
              },
            },
          ],
        })
          .then(({ errors }) => {
            if (errors) {
              throw errors;
            }
            Analytics.taskSucceeded({
              taskName,
              traceId,
              source,
            });
            Analytics.sendTrackEvent({
              action: 'deleted',
              actionSubject: 'workspaceAccessRequest',
              source,
              containers: {
                organization: {
                  id: orgId,
                },
              },
              attributes: {
                requestor: memberId,
              },
            });
          })
          .catch((error) => {
            Analytics.taskFailed({
              traceId,
              taskName,
              source,
              error,
            });

            showFlag({
              id: 'deleteRequestAccessToJoinError',
              title: intl.formatMessage({
                id: 'templates.workspace_join_requests.could-not-connect-to-trello',
                defaultMessage: 'Could not connect to Trello.',
                description:
                  'Flag title when there is an error deleting a request',
              }),
              appearance: 'error',
              actions: [
                {
                  content: intl.formatMessage({
                    id: 'templates.workspace_join_requests.reload-page',
                    defaultMessage: 'Reload page',
                    description:
                      'Action to reload the page when there is an error deleting a request',
                  }),
                  onClick: () => window.location.reload(),
                  type: 'link',
                },
              ],
            });
            sendErrorEvent(error, {
              tags: {
                ownershipArea: 'trello-web-eng',
                feature: 'Workspace Membership Request',
              },
              extraData: {
                component: 'WorkspaceJoinRequestList',
              },
            });
          }),
      );
    }
    Promise.all(deleteAccessRequestPromises).then(() => {
      toggleDeletePopover();
      uncheckAll();
    });
  }, [
    orgId,
    checkedRequests,
    deleteAccessRequest,
    accessRequests,
    intl,
    toggleDeletePopover,
    uncheckAll,
  ]);

  const getAddSelectedTooltip = useCallback(() => {
    if (
      !isAddSelectedButtonEnabled &&
      isOverLimit &&
      checkedRequests.length > 0
    ) {
      return (
        <Tooltip
          content={intl.formatMessage({
            id: 'templates.workspace_join_requests.this-workspace-is-over-the-limit-change-selection',
            defaultMessage:
              'This free Workspace is over the 10 collaborator limit. Try changing your selection or upgrade this Workspace for unlimited collaborators.',
            description:
              'Tooltip for add selected to workspace button when already over the limit',
          })}
          tag="span"
          position="top"
        >
          <HelpIcon />
        </Tooltip>
      );
    }
    if (!isAddSelectedButtonEnabled && checkedRequests.length > 0) {
      return (
        <Tooltip
          content={intl.formatMessage({
            id: 'templates.workspace_join_requests.adding-all-would-exceed-limit',
            defaultMessage:
              'Adding all of these people would exceed the free Workspace 10 collaborator limit. Try changing your selection or upgrade this Workspace for unlimited collaborators.',
            description: 'Tooltip for add selected to workspace button',
          })}
          tag="span"
          position="top"
        >
          <HelpIcon />
        </Tooltip>
      );
    }
    return null;
  }, [checkedRequests.length, intl, isAddSelectedButtonEnabled, isOverLimit]);

  return (
    <div
      className={styles.container}
      data-testid={getTestId<WorkspaceRequestAccessTestIds>(
        'ws-join-request-list',
      )}
    >
      <WorkspaceMembersFilterBox onNameFilterChange={onNameFilterChange} />
      <div className={styles.multiSelectControls}>
        <div className={styles.leftControls}>
          <div className={styles.selectAllCheckboxContainer}>
            <Checkbox
              isChecked={selectAllCheckbox === 'all'}
              isIndeterminate={selectAllCheckbox === 'partial'}
              className={styles.checkbox}
              id="selectAll"
              onChange={handleSelectAll}
            />
            <a
              href="#"
              className={styles.selectAllLabel}
              onClick={handleSelectAll}
            >
              {checkedRequests.length > 0 ? (
                <FormattedMessage
                  id="templates.workspace_join_requests.count-selected"
                  defaultMessage="{count} selected"
                  description="Title of checkbox when X number of access requests are selected"
                  values={{ count: checkedRequests.length }}
                />
              ) : (
                <FormattedMessage
                  id="templates.workspace_join_requests.select-all-count"
                  defaultMessage="Select all ({count})"
                  description="Title of checkbox to select all access requests"
                  values={{ count: accessRequests.length }}
                />
              )}
            </a>
          </div>
        </div>
        <div className={styles.rightControls}>
          <Button
            appearance="primary"
            className={styles.addSelected}
            ref={triggerRefApproveWorkspaceJoinRequestPopover}
            onClick={onClickAddAllToWorkspace}
            isDisabled={!isAddSelectedButtonEnabled}
          >
            <FormattedMessage
              id="templates.workspace_join_requests.add-selected-to-workspace"
              defaultMessage="Add selected to Workspace"
              description="Button to add selected access requests to the workspace"
            />
            {getAddSelectedTooltip()}
          </Button>
          <WorkspaceJoinRequestPopover
            {...approveWorkspaceJoinRequestPopoverProps}
            isMultipleSelected={true}
            isDelete={false}
            onHide={hideApproveWorkspaceJoinRequestPopover}
            onClick={onConfirmAddToWorkspace}
            orgType={orgType}
            orgId={orgId}
          />
          <Button
            className={styles.deleteSelected}
            onClick={onClickDelete}
            ref={triggerRefDeletePopover}
            isDisabled={!isDeleteSelectedButtonEnabled}
          >
            <FormattedMessage
              id="templates.workspace_join_requests.delete-selected-requests"
              defaultMessage="Delete selected requests"
              description="Button to delete selected access requests"
            />
          </Button>
          <WorkspaceJoinRequestPopover
            {...deletePopoverProps}
            isMultipleSelected={true}
            isDelete={true}
            onHide={hideDeletePopover}
            onClick={onConfirmDelete}
            orgType={orgType}
            orgId={orgId}
          />
        </div>
      </div>
      {isSelectedRequestsExceedLimitMessageVisible && (
        <SelectedRequestsExceedLimitMessage workspaceId={orgId} />
      )}
      <hr role="presentation" className={styles.multiSelectControlsDivider} />
      <section className={styles.memberList}>
        {accessRequests.map(({ idMemberRequestor, signature, timestamp }) => {
          const isMemberWorkspaceGuest = !!guestMap[idMemberRequestor];
          const isAddToWorkspaceEnabled =
            isMemberWorkspaceGuest || !isAtOrOverLimit;
          return (
            <WorkspaceJoinRequestItem
              key={idMemberRequestor}
              orgId={orgId}
              checkboxes={checkboxes}
              requestWorkspaceAccessDate={formatDate(
                new Date(timestamp),
                'PPP',
              )}
              memberId={idMemberRequestor}
              signature={signature}
              orgType={orgType}
              showErrorFlag={showErrorFlag}
              showSuccessFlag={showSuccessFlag}
              orgIdEnterprise={orgIdEnterprise}
              orgEnterpriseName={orgEnterpriseName}
              nameFilter={nameFilter}
              isMemberWorkspaceGuest={isMemberWorkspaceGuest}
              onCheckboxChange={onIndividualCheckboxChange}
              isAddToWorkspaceEnabled={isAddToWorkspaceEnabled}
              isWorkspaceOverLimit={isOverLimit}
            />
          );
        })}
      </section>
    </div>
  );
};
